import React, { useCallback, useEffect, useState } from 'react'

import Formsy from 'formsy-react';
import { Plus, Trash } from 'lucide-react';
import { FormBody, Row, Col, Button, Label, FormGroup, Input as TWInput, DefaultTooltip, TabsList, Tabs, TabsTrigger, TabsContent, FormSection, SelectMulti, FormFooter, FormCard, DefaultTable, Separator, ButtonIcon } from 'twyne-new.ui';

import PageVariablesEditorFrame from '@Components/Mergetag/PageVariablesEditorFrame'
import { buttonLabels } from '@Components/rules/options';
import CodeEditor from 'components/form/CodeEditor';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import Slider from 'components/form/Slider';
import Switch from 'components/form/Switch';
import Rules from 'components/rules/Rules';
import history from 'utils/history';

import { usePixelStore } from '../store';

const typeOptions = [
  { value: 'html', label: 'HTML' },
  { value: 'http_get', label: 'HTTP GET' },
  { value: 'http_post', label: 'HTTP POST' },
  { value: 'twyne_offer', label: 'Twyne Offer' },
]

class PixelForm extends React.Component {
  state = {
    type: '',
    throttle: 100,
    postData: [],
    conflictEvent: null,
  };

  handleConflict = event => {
    this.setState({ conflictEvent: event })
  }

  componentDidMount() {
    const { data } = this.props;
    if (data) {
      if (data.postData) {
        data.postData = Object.entries(JSON.parse(data.postData));
      } else {
        data.postData = [];
      }
      if (data.type === 'twyne_offer' && data.pages) {
        data.pages = data.pages.map(({ id }) => id)
      }
      this.setState(data);
    }
  }

  handleClickAddPostData = () => {
    const { postData } = this.state;
    postData.push(['', '']);
    this.setState({ postData });
  }

  handleCickRemovePostData = index => () => {
    const { postData } = this.state;
    postData.splice(postData.indexOf(index), 1);
    this.setState({ postData });
  }

  handleChangePostDataInput = (index, objIndex) => event => {
    const { postData } = this.state;
    postData[index][objIndex] = event.target.value;
    this.setState({ postData });
  }

  handleOnChangeType = option => {
    const type = option ? option.value : '';
    this.setState({ type });
  }

  handleChangeSlotPosition = option => {
    const slotPosition = option ? option.value : null;
    this.setState({ slotPosition });
  }

  handleValidSubmit = model => {
    const { type } = this.state
    let payload = { ...this.state, ...model }

    switch (type) {
      case 'html':
        payload = { ...payload, pages: [], postData: null }
        break;
      case 'http_get':
        payload = { ...payload, content: null, pages: [], postData: null }
        break;
      case 'http_post':
        payload = { ...payload, content: null, pages: [] }
        payload.postData = JSON.stringify(Object.assign(...this.state.postData.map(d => ({ [d[0]]: d[1] }))) || '')
        break;
      case 'twyne_offer':
        payload = { ...payload, rules: null, postData: null }
        break;
      default:
        break;
    }

    this.props.handleSubmit(payload)
  }

  Editor = ({ mode }) => {
    const [isValid, setIsValid] = useState(true)

    const handleValidate = useCallback(annotations => {
      const error = annotations?.some(({ type }) => type === 'error')
      setIsValid(!error)
    }, [])

    return <PageVariablesEditorFrame mode={mode}>
      <CodeEditor
        className="h-[25vh]"
        required
        name="content"
        language={mode}
        width="100%"
        value={this.state.content || ''}
        setOptions={{
          enableBasicAutocompletion: true,
          enableLiveAutocompletion: true,
        }}
        onValidate={handleValidate}
        validationErrors={{ syntaxError: "Syntax error, check editor" }}
        validations={{ syntaxError: () => isValid }}
      />
    </PageVariablesEditorFrame>
  }

  ContentTab = () => {
    const { type } = this.state

    if (!['html', 'twyne_offer'].includes(this.state.type)) {
      return null;
    }

    return <Row>
      <Col size="12">
        <FormGroup>
          <Label>Script</Label>
          <this.Editor mode={type === "html" ? "html" : "javascript"} />
        </FormGroup>
      </Col>
    </Row>
  }

  HttpPostTab = () => {
    const { type, postData } = this.state

    if (!type.startsWith('http')) {
      return null;
    }

    const columns = [{
      dataField: '0',
      text: 'Key',
      formatter: (col, _row, index) => <TWInput
        value={col}
        onChange={this.handleChangePostDataInput(index, 0)}
        placeholder="Key"
        required
      />,
    }, {
      dataField: '1',
      text: 'Value',
      formatter: (col, _row, index) => <TWInput
        value={col}
        onChange={this.handleChangePostDataInput(index, 1)}
        placeholder="Value"
        required
      />,
    }, {
      dataField: '',
      text: '',
      formatter: (_col, _row, index) => <ButtonIcon
        content='Delete'
        onClick={this.handleCickRemovePostData(index)}
      >
        <Trash size={16} />
      </ButtonIcon>,
      className: 'w-8',
    }]

    return <FormCard
      title='Post Data'
      headerButtons={<Button size="sm" color="accent" onClick={this.handleClickAddPostData}><Plus size={14} /> Add</Button>}
      render={postData?.length}
    >
      <Separator />
      <div className='max-h-100 overflow-y-auto'>
        <DefaultTable
          data={postData}
          columns={columns}
          noDataIndication={() => 'No content to show.'}
        />
      </div>
    </FormCard>
  }

  RulesTab = () => {
    const { rules } = this.state

    return <FormCard render={!!rules} title='Rules' headerButtons={<div className='flex items-center gap-2'>
      <Switch
        name="_"
        value={!!rules}
        setValue={active => {
          if (active) {
            this.setState({ rules: { condition: 'and', groups: [] } })
          } else {
            this.setState({ rules: null })
          }
        }}
      />
      <Label>Active</Label>
    </div>}>
      <Rules
        origin='pixel'
        rules={rules}
        handleChange={state => this.setState(state)}
        disabled={!rules}
        onConflict={this.handleConflict}
      />
    </FormCard>
  }

  PageTab = () => {
    const [pages, setPages] = useState([])
    const { lists: { pages: { items, useLoad } } } = usePixelStore()

    useLoad(PARAMS)

    useEffect(() => {
      if (items) {
        const pages = items.map(({ id, name }) => ({ value: id, label: `[#${id}] ${name}` }))
        setPages(pages)
      }
    }, [items])

    if (this.state.type !== 'twyne_offer') {
      return null;
    }

    return <Row>
      <Col size="12">
        <FormGroup>
          <Label>Pages where this pixel should render</Label>
          <SelectMulti options={pages} value={this.state.pages}
            onChange={options => this.setState({ pages: options.map(({ value }) => value) })} />
        </FormGroup>
      </Col>
    </Row>
  }

  Tabs = () => {
    const [tabs, setTabs] = useState([[]])
    const { type } = this.state

    useEffect(() => {
      const newTabs = []

      if (['html', 'twyne_offer'].includes(type)) {
        newTabs.push(['content', 'Content'])
      }

      if (type === 'http_post') {
        newTabs.push(['http_details', 'HTTP Details'])
      }

      if (type === 'twyne_offer') {
        newTabs.push(['pages', 'Pages'])
      } else {
        newTabs.push(['rules', 'Rules'])
      }

      setTabs(newTabs)
    }, [type])

    return <>
      {tabs.map(([tab], index) => <Col key={index}>
        <this.FormCardContent type={tab} />
      </Col>)}
    </>
  }

  FormCardContent = ({ type }) => {
    const contents = {
      http_details: <this.HttpPostTab />,
      content: <FormCard title='Content'><this.ContentTab /></FormCard>,
      rules: <this.RulesTab />,
      pages: <FormCard title='Pages'><this.PageTab /></FormCard>,
    }

    return <>
      {contents[type]}
    </>
  }

  render() {
    const { conflictEvent, type } = this.state
    const disableForm = conflictEvent === 'conflict' || conflictEvent === 'checking'
    return <Formsy onValidSubmit={this.handleValidSubmit}>
      <FormBody>
        <Row>
          <Col size='12'>
            <FormCard title='General'>
              <Row>
                <Col size="4">
                  <FormGroup>
                    <Label>Pixel Name</Label>
                    <Input name="name" value={this.state.name || ''} required />
                  </FormGroup>
                </Col>
                <Col size="4">
                  <FormGroup>
                    <Label>Type</Label>
                    <Select name="type" value={this.state.type} options={typeOptions}
                      handleOnChange={this.handleOnChangeType} required />
                  </FormGroup>
                </Col>
                <Col size="4">
                  <FormGroup>
                    <Label>Throttle</Label>
                    <Slider className='pt-3' name="throttle" min={1} max={100} value={this.state.throttle} />
                  </FormGroup>
                </Col>
                {['http_get', 'http_post'].includes(type) && <Col size="12">
                  <FormGroup>
                    <Label>URL</Label>
                    <Input name="url" value={this.state.url || ''} />
                  </FormGroup>
                </Col>}
              </Row>
            </FormCard>
          </Col>
          <this.Tabs />
        </Row>
      </FormBody>
      <FormFooter>
        <DefaultTooltip side="top" content={disableForm && "You can't save the pixel with rules conflict!"}>
          <Button type="submit" disabled={disableForm} color='accent'>{buttonLabels[conflictEvent] || 'Save'}</Button>
        </DefaultTooltip>
        <Button onClick={() => history.goBack()} variant='ghost'>Cancel</Button>
      </FormFooter>
    </Formsy>
  }
}

export default PixelForm;

const PARAMS = { type: ['partner-twyne', 'partner-ifficient'], active: true }
